import { useEffect } from 'react';
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  QueryKey,
} from '@tanstack/react-query';
import { useUserStore } from '@/store/users';
import { authService } from '@ygoargentina/sdk/api/auth-service';
import { confirmSessionLost } from '@/helpers/session-loss';

// Check if error is an authentication error (401)
export const isAuthError = (error: unknown): boolean => {
  if (!error) return false;
  const err = error as Error & { response?: { status?: number } };
  const message = err.message?.toLowerCase() || '';

  return (
    message.includes('401') ||
    message.includes('unauthorized') ||
    message.includes('unauthenticated') ||
    err.response?.status === 401
  );
};

// Redirect to login page — sólo si el API confirma que no hay sesión: un
// `unauthorized` puede ser un permiso negado (ver `confirmSessionLost`).
export const redirectToLogin = async (origin = 'auth_query') => {
  if (!(await confirmSessionLost(origin))) return;

  const { setUser } = useUserStore.getState();
  setUser(null);
  // Clear CSRF token to prevent stale cookies from causing loops
  authService.clearCsrfToken();
  // Redirect directly to login
  window.location.href = window.location.origin + '/login';
};

/**
 * A wrapper around useQuery that automatically handles 401 errors
 * by redirecting to the login page.
 */
export function useAuthQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
): UseQueryResult<TData, TError> {
  const result = useQuery({
    ...options,
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (isAuthError(error)) return false;
      // Use provided retry or default to 3
      if (typeof options.retry === 'function') {
        return options.retry(failureCount, error);
      }
      if (typeof options.retry === 'number') {
        return failureCount < options.retry;
      }
      return failureCount < 3;
    },
  });

  // Handle auth errors - redirect to login
  useEffect(() => {
    if (result.error && isAuthError(result.error)) {
      void redirectToLogin('use_auth_query');
    }
  }, [result.error]);

  return result;
}

export default useAuthQuery;
